// Entry point.
//
// The widget now opens as a centered modal instead of pushing host-page
// content down inline. The embed contract grows accordingly:
//
//   1. Any element with `data-cc-booking-widget` continues to work as before
//      — it becomes a click trigger that opens the modal. If the element is
//      empty, a default "Book a Fitting" button is rendered inside it.
//
//   2. Any element with `data-cc-book-now` is also wired as a trigger. Hosts
//      can drop multiple triggers on a single page (hero CTA, sticky nav,
//      footer button, mid-page banner) and they all open the same modal.
//
//   3. The React tree mounts inside a hidden container appended to <body>.
//      The modal portal renders on top of the page when opened, so the
//      mount point's position on the page is irrelevant.
//
// Embed contract: see ../../clubchampion-docs/architecture/widget-embedding-contract.md

import { createRoot } from 'react-dom/client';
import { Widget } from './widget';
import { ENGAGE_EVENT } from './state/events';

const TRIGGER_SELECTORS = ['[data-cc-booking-widget]', '[data-cc-book-now]'];
const DEFAULT_TRIGGER_TEXT = 'Book a Fitting';

function fireEngage(source: string): void {
  // Reuse the existing ENGAGE_EVENT contract so analytics + scroll
  // behavior already wired into the widget continues to work. Raw
  // window.dispatchEvent (not the typed emit() helper) because the
  // engage event isn't in WidgetEventDetails — it's a host→widget
  // signal, not a widget→host one.
  window.dispatchEvent(new CustomEvent(ENGAGE_EVENT, { detail: { source } }));
}

function wireTriggers(): void {
  const seen = new WeakSet<Element>();
  for (const selector of TRIGGER_SELECTORS) {
    for (const el of Array.from(document.querySelectorAll(selector))) {
      if (seen.has(el)) continue;
      seen.add(el);

      const source = el.getAttribute('data-cc-trigger-source') ?? el.id ?? selector;

      // For empty [data-cc-booking-widget] divs (the legacy placeholder),
      // render a default trigger button inside so the host page has
      // something visible to click. Pages that already supply their own
      // button (e.g. hero CTA with custom copy) are left alone.
      if (
        selector === '[data-cc-booking-widget]' &&
        el instanceof HTMLElement &&
        el.childNodes.length === 0
      ) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cc-default-trigger';
        btn.textContent = DEFAULT_TRIGGER_TEXT;
        // Inline minimal styling so the button looks reasonable even on
        // hosts that haven't loaded the widget's CSS module yet. Hosts
        // can override by styling [data-cc-booking-widget] > .cc-default-trigger.
        btn.style.cssText = [
          'background:#193b3f',
          'color:#ffffff',
          'border:none',
          'border-radius:999px',
          'padding:12px 24px',
          'font-size:15px',
          'font-weight:600',
          'cursor:pointer',
          'font-family:inherit',
        ].join(';');
        el.appendChild(btn);
      }

      el.addEventListener('click', (e) => {
        // Don't hijack clicks on internal anchors / form controls inside
        // the trigger; only the trigger element itself opens the modal.
        const target = e.target as Element | null;
        if (target && target !== el && target.tagName === 'A' && target.getAttribute('href')) {
          return;
        }
        e.preventDefault();
        fireEngage(source);
      });
    }
  }
}

function mountWidget(): void {
  const embedSource = window.CC_WIDGET_CONFIG?.embedSource ?? 'unknown';

  // Single mount container appended to body — the modal portals render
  // on top of the page regardless of where this lives.
  const host = document.createElement('div');
  host.setAttribute('data-cc-widget-host', '');
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(<Widget embedSource={embedSource} />);
}

function init(): void {
  mountWidget();
  wireTriggers();

  // Re-wire when host pages add triggers dynamically (single-page apps,
  // late-rendered hero sections). Debounced via microtask to coalesce
  // bursts of mutations.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      wireTriggers();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
